import React, { useState } from 'react';
import { Menu } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faKey } from '@fortawesome/free-solid-svg-icons';
import { useEncryption } from '../context/EncryptionContext';

const EncryptionSettings = () => {
  const [isAbortingTransition, setIsAbortingTransition] = useState(false);
  const { isEncryptionEnabled, toggleEncryption, hasCloudKey, setShowCloudKeyModal, encryptionPolicy, encryptionError, isEncryptionTransitioning, abortActiveEncryptionTransition } = useEncryption();
  const policyUsable = encryptionPolicy?.kind === 'legacy' || encryptionPolicy?.kind === 'managed';
  const blocked = !policyUsable || isEncryptionTransitioning;
  const showCloudKeyEntry = isEncryptionEnabled || (isEncryptionTransitioning && hasCloudKey);

  const handleToggleEncryption = (close) => {
    if (blocked) return;
    toggleEncryption();
    close(); // Close the menu after toggling
  };

  const handleOpenModal = (close) => {
    if (!policyUsable) return;
    setShowCloudKeyModal(true);
    close(); // Close the menu after opening modal
  };

  const handleAbortTransition = async () => {
    if (!isEncryptionTransitioning || isAbortingTransition) return;
    setIsAbortingTransition(true);
    try {
      await abortActiveEncryptionTransition();
    } finally {
      setIsAbortingTransition(false);
    }
  };

  return (
    <>
      <Menu.Item>
        {({ active, close }) => (
          <button
            onClick={() => handleToggleEncryption(close)}
            disabled={blocked}
            aria-describedby={blocked ? 'encryption-policy-status' : undefined}
            className={`${
              active ? 'bg-gray-100 dark:bg-gray-700' : ''
            } group flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
          >
            <FontAwesomeIcon icon={faLock} className="mr-3" />
            {isEncryptionEnabled ? 'Disable' : 'Enable'} Experimental Encryption
          </button>
        )}
      </Menu.Item>
      {(isEncryptionTransitioning || !policyUsable || encryptionError) && (
        <div id="encryption-policy-status" role="status" className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
          {isEncryptionTransitioning
            ? `${encryptionError ? `${encryptionError} ` : ''}Abort the active encryption change, then start it again.`
            : !policyUsable
              ? 'Encryption controls are unavailable until the profile can be verified.'
              : encryptionError}
        </div>
      )}
      {isEncryptionTransitioning && (
        <Menu.Item>
          {() => (
            <button
              onClick={handleAbortTransition}
              disabled={isAbortingTransition}
              className="group flex items-center w-full px-4 py-2 text-sm text-red-700 dark:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAbortingTransition ? 'Aborting encryption change...' : 'Abort encryption change'}
            </button>
          )}
        </Menu.Item>
      )}
      {showCloudKeyEntry && (
        <Menu.Item>
          {({ active, close }) => (
            <button
              onClick={() => handleOpenModal(close)}
              disabled={!policyUsable}
              className={`${
                active ? 'bg-gray-100 dark:bg-gray-700' : ''
              } group flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
            >
              <FontAwesomeIcon icon={faKey} className="mr-3" />
              {isEncryptionTransitioning ? 'Load Cloud Key for Recovery' : hasCloudKey ? 'Manage Cloud Key' : 'Setup Cloud Key'}
            </button>
          )}
        </Menu.Item>
      )}
    </>
  );
};

export default EncryptionSettings;
